"use client";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@perk/core/lib/utils";
import { LoadingDots } from "./loading-dots";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-xs font-bold font-sans-condensed transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 !rounded-button",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-content shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-primary bg-background text-primary shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-content shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        icon: "bg-primary text-primary-content shadow hover:bg-primary/90",
        underline:
          "bg-transparent border-none hover:bg-transparent underline underline-offset-4 decoration-2 text-primary decoration-header-bg hover:decoration-primary",
      },
      size: {
        default: "h-9 px-7.5 py-4 text-xs",
        icon: "h-9 px-7.5 py-4 text-xs",
        sm: "h-8 rounded-md py-2 px-5 text-xs",
        tiny: "h-8 rounded-md py-2 px-5 text-xs",
        tinyIcon: "h-8 rounded-md py-2 px-5 font-normal text-xs",
        lg: "h-10 rounded-md px-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText: _loadingText,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const { t } = useTranslation();
    const loadingText = _loadingText ?? t("Loading");

    const ariaLabel = props["aria-label"];
    // For icon buttons without visible text, ensure there's an aria-label
    const needsAriaLabel = variant === "icon" && !props.children && !ariaLabel;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={loading}
        aria-label={needsAriaLabel ? "Button" : ariaLabel}
        aria-busy={loading}
        {...props}
      >
        {loading ? (
          <>
            {_loadingText && <span className="mr-2">{loadingText}</span>}
            <LoadingDots aria-hidden="true" />
          </>
        ) : (
          props.children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
